import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { ShoppingBag, TrendingUp, CheckCircle, ArrowLeft, Calendar, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#6366f1'];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activePreset, setActivePreset] = useState('30d');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    const token = sessionStorage.getItem('token');
    try {
      let url = `${API}/admin/analytics`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', new Date(startDate).toISOString());
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.append('end_date', end.toISOString());
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError(error.response?.data?.detail || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const applyPreset = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    let start = new Date();
    
    if (preset === '7d') {
      start.setDate(now.getDate() - 7);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (preset === '30d') {
      start.setDate(now.getDate() - 30);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (preset === '90d') {
      start.setDate(now.getDate() - 90);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setActivePreset('all');
  };

  if (loading && !data) {
    return (
      <PageLoadingState title="Loading analytics" />
    );
  }

  const orderStatusData = data ? Object.entries(data.orders_by_status).map(([name, value]) => ({ name, value })) : [];
  const revenueStatusData = data ? Object.entries(data.revenue_by_status).map(([name, value]) => ({ name, value })) : [];
  
  const totalOrders = data ? Object.values(data.orders_by_status).reduce((a, b) => a + b, 0) : 0;
  const deliveredRevenue = data?.revenue_by_status?.delivered || 0;
  const completedOrders = data?.orders_by_status?.delivered || 0;

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="p-2 hover:bg-secondary rounded-full transition-colors">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-4xl md:text-5xl font-semibold tracking-tight">Analytics</h1>
                <p className="text-muted-foreground mt-1">Sales performance and data analysis</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex bg-secondary/50 p-1 rounded-lg border border-white/5">
                {[
                  { id: '7d', label: '7D' },
                  { id: '30d', label: '30D' },
                  { id: '90d', label: '90D' },
                  { id: 'all', label: 'All' }
                ].map((p) => (
                  <button
                    key={p.id}
                    onClick={() => applyPreset(p.id)}
                    className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all ${
                      activePreset === p.id 
                        ? 'bg-primary text-primary-foreground shadow-lg' 
                        : 'hover:bg-white/5 text-muted-foreground'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center gap-2 glass-morphic px-3 py-1.5 rounded-lg border border-white/5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => { setStartDate(e.target.value); setActivePreset(''); }}
                  className="bg-transparent border-none text-xs focus:ring-0 text-foreground outline-none"
                />
                <span className="text-muted-foreground text-xs">to</span>
                <input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => { setEndDate(e.target.value); setActivePreset(''); }}
                  className="bg-transparent border-none text-xs focus:ring-0 text-foreground outline-none"
                />
                {(startDate || endDate) && (
                  <button onClick={clearFilters} className="ml-1 p-1 hover:bg-white/10 rounded-full transition-colors">
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-xl mb-8 flex items-center justify-between">
              <p>{error}</p>
              <Button variant="ghost" size="sm" onClick={fetchAnalytics}>Retry</Button>
            </div>
          )}

          {data ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="glass-morphic rounded-2xl p-6 border border-white/10"
                >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <ShoppingBag className="h-8 w-8 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Orders</p>
                        <p className="text-3xl font-bold">{totalOrders}</p>
                      </div>
                    </div>
                </motion.div>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="glass-morphic rounded-2xl p-6 border border-white/10"
                >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-green-500/10 rounded-xl">
                        <TrendingUp className="h-8 w-8 text-green-500" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Delivered Revenue</p>
                        <p className="text-3xl font-bold">₹{deliveredRevenue.toLocaleString()}</p>
                      </div>
                    </div>
                </motion.div>
                <motion.div 
                  whileHover={{ y: -5 }}
                  className="glass-morphic rounded-2xl p-6 border border-white/10"
                >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-accent/10 rounded-xl">
                        <CheckCircle className="h-8 w-8 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Completed Orders</p>
                        <p className="text-3xl font-bold">{completedOrders}</p>
                      </div>
                    </div>
                </motion.div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                <div className="glass-morphic rounded-2xl p-6 h-[450px] border border-white/10">
                  <h3 className="text-xl font-semibold mb-6">Sales Trend</h3>
                  <ResponsiveContainer width="100%" height="85%">
                    <LineChart data={data.sales_trend}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                      <XAxis 
                        dataKey="_id" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                        dy={10}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                        itemStyle={{ color: '#fff' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="daily_revenue" 
                        stroke="#8b5cf6" 
                        strokeWidth={4} 
                        dot={{ r: 4, fill: '#8b5cf6', strokeWidth: 2, stroke: '#fff' }} 
                        activeDot={{ r: 8, strokeWidth: 0 }} 
                        name="Revenue" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="glass-morphic rounded-2xl p-6 h-[450px] border border-white/10">
                  <h3 className="text-xl font-semibold mb-6">Orders by Status</h3>
                  <ResponsiveContainer width="100%" height="85%">
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={120}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                      />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="glass-morphic rounded-2xl p-6 h-[450px] border border-white/10">
                  <h3 className="text-xl font-semibold mb-6">Top 5 Selling Products</h3>
                  {data.top_products.length > 0 ? (
                    <ResponsiveContainer width="100%" height="85%">
                      <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis type="number" hide />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          width={120} 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 10 }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                        />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Units Sold" barSize={30} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">No data for this period</div>
                  )}
                </div>

                <div className="glass-morphic rounded-2xl p-6 h-[450px] border border-white/10">
                  <h3 className="text-xl font-semibold mb-6">Revenue by Status</h3>
                  <ResponsiveContainer width="100%" height="85%">
                    <BarChart data={revenueStatusData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px' }}
                      />
                      <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} name="Revenue" barSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center glass-morphic rounded-2xl border border-white/10">
              <p className="text-muted-foreground">Loading data...</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
